// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from 'react'
import type { QueryOperator } from '../../../core/operators/QueryOperator'
import type { QueryResult } from '../../../core/operators/types'

/** Stable empty result for null operators — avoids allocating per render */
const EMPTY_RESULT: QueryResult<never> = { data: [], isLoading: false, error: null }

/** Stable loading result for operator transitions — avoids allocating per render */
const LOADING_RESULT: QueryResult<never> = { data: [], isLoading: true, error: null }

/**
 * React hook for framework-agnostic QueryOperator
 *
 * Subscribes to a QueryOperator's observable and manages state with React hooks.
 * Handles null operators gracefully and cleans up subscriptions on unmount.
 *
 * @param queryOperator - QueryOperator instance or null
 * @returns QueryResult with data, isLoading, and error
 *
 * @example
 * ```tsx
 * function TaskList() {
 *   const db = useDatabase();
 *   const queryOperator = useMemo(() => {
 *     if (!db) return null;
 *     const taskOps = new TaskOperator(db);
 *     return taskOps.query({ orgId: 'org_123', status: 'in_progress' });
 *   }, [db]);
 *
 *   const result = useQuery(queryOperator);
 *
 *   if (result.isLoading) return <div>Loading...</div>;
 *   if (result.error) return <div>Error: {result.error.message}</div>;
 *
 *   return (
 *     <ul>
 *       {result.data.map(task => (
 *         <li key={task.id}>{task.title}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useQuery<T>(queryOperator: QueryOperator<T> | null): QueryResult<T> {
  // Bundle the result together with the operator that produced it so we can
  // detect operator transitions purely from React state (no refs needed).
  const [state, setState] = useState<{
    result: QueryResult<T>
    operator: QueryOperator<T> | null
  }>({
    result: { data: [], isLoading: true, error: null },
    operator: null,
  })

  useEffect(() => {
    if (!queryOperator) {
      return
    }

    // Subscribe to operator's observable — setState only in callbacks
    const subscription = queryOperator.observable.subscribe({
      next: result => setState({ result, operator: queryOperator }),
      error: error =>
        setState({
          result: { data: [], isLoading: false, error },
          operator: queryOperator,
        }),
    })

    // Cleanup subscription on unmount or when operator changes
    return () => {
      subscription.unsubscribe()
      queryOperator.destroy()
    }
  }, [queryOperator])

  // Null operator: no data, not loading
  if (!queryOperator) {
    return EMPTY_RESULT as QueryResult<T>
  }

  // Operator changed but subscription hasn't emitted yet: show loading.
  // This prevents stale { data: oldResults, isLoading: false } from being
  // visible between cleanup and the new subscription's first emission,
  // which would cause consumers like OrgWorkspaceContext to prematurely
  // mark restore as attempted.
  if (state.operator !== queryOperator) {
    return LOADING_RESULT as QueryResult<T>
  }

  return state.result
}
